<template>
    <flex-element :element-type="elementType" tag="ul" class-default-inline="f-flex f-justify-center f-flex-row mob:f-flex-col f-flex-wrap f-w-1/1">
        <collection-item
            v-for="(element, index) in elementsNew"
            :key="element.id"
            v-group:list-item
            :class-default="previousClasses || defaultClasses"
            :index="index"
            :display-name="titleCase(element.elementType)"
            v-bind="element"
            :pointer="index + 1"
            is-dynamic-element
            @element-updated="elementUpdated"
            @classes-updated="onItemClassUpdate"
            @delete-element="deleteElement"
            @move-element="moveElement"
        />

        <droppable
            v-if="isContentEditable"
            class="f-w-1/4 f-p-0.5 lap:f-w-1/3 tab:f-w-1/2 mob:f-w-1/1"
            :class="dropZoneClass"
            :filled="!!elementsNew.length"
            force-display
            :mode="dropZoneMode"
            :text="dropZoneText"
            element-type="droppable"
            @drop="data => onDrop(data)"
            @click.native.stop="onDropZoneClick"
        />
    </flex-element>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import uuidv4 from 'uuid/v4';

import baseOrganism from '@/mixins/base-organism/base-organism-smart';
import { Organism } from '@/data/data-models';
import CollectionItem from '@/components-user/smart/containers/collection-item';
import { SHOW_SNACKBAR, CLEAR_SELECTED_ELEMENTS } from '@/store/types/mutation-types';
import { generateRendering } from '@/utils/rendering';
import flexDeepCopy from '@/utils/flex-deep-copy';
import { STATE_CLASSES } from '@/data/constants/classes';
import mapFilters from '@/utils/map-filters';

const CUSTOM_ATTR_NAME = 'itemType';

export default {
    components: { CollectionItem },
    mixins: [baseOrganism],

    props: {
        displayName: {
            type: String,
            default: 'Collection',
        },
    },

    data() {
        return {
            defaultClasses: 'f-relative f-w-1/4 f-p-0.5 lap:f-w-1/3 tab:f-w-1/2 mob:f-w-1/1',
            previousClasses: '',

            itemName: '',
            itemType: '',
        };
    },

    computed: {
        ...mapState({
            selectedElements: state => state.page.selectedElements,
        }),

        dropZoneClass() {
            return this.elementsNew.length ? STATE_CLASSES.CLICKABLE : '';
        },

        dropZoneMode() {
            return this.elementsNew.length ? 'click' : 'drop';
        },

        dropZoneText() {
            return this.elementsNew.length ? `add ${ this.itemName }` : 'drop item';
        },
    },

    beforeMount() {
        if (this.hasElementData(CUSTOM_ATTR_NAME)) {
            this.itemType = this.getElementData(CUSTOM_ATTR_NAME);
        }
    },

    methods: {
        ...mapFilters(['titleCase']),

        ...mapMutations({
            clearSelectedElements: CLEAR_SELECTED_ELEMENTS,
            showSnackbar: SHOW_SNACKBAR,
        }),

        deleteElement(index) {
            this.elementsNew.splice(index, 1);

            this.clearSelectedElements();

            if (!this.elementsNew.length) {
                this.itemName = '';
                this.itemType = '';
            }

            this.setChangedData(true);
        },

        generateElement(itemType, elementData) {
            // until drop zone index changes, we go by length
            const pointer = this.elementsNew.length + 1;

            const element = new Organism({
                id: uuidv4(),
                name: `collection-item_${ pointer }`,
                elementType: 'collection-item',
                pointer,
                classes: '',
                templateType: 'Organism',
                dataSourceId: '00000000-0000-0000-0000-000000000000',
                elements: [elementData],
            });

            this.elementsNew.push(element);
        },

        async moveElement(direction, index) {
            let to = index;

            if (direction === 'up') {
                to = index - 1;
            } else {
                to = index + 1;
            }

            if (to < 0 || to === this.elementsNew.length) return;

            const element = this.elementsNew[index];

            this.elementsNew.splice(index, 1);
            this.elementsNew.splice(to, 0, element);
            this.setChangedData(true);

            await this.$nextTick();

            this.$root.$emit('flex::component::move');
        },

        onDropZoneClick() {
            if (!this.itemType) return;

            const lastElement = flexDeepCopy(this.elementsNew[this.elementsNew.length - 1]);
            this.elementsNew.push(lastElement);
            this.setChangedData(true);
        },

        async onDrop(data) {
            // we want to "lock" the collection into only accepting items of the same type ("itemType")
            // the itemType prop is only set if (a) it hasn't already been set or (b) the user has deleted all existing items
            if (!this.itemType || !this.elementsNew.length) {
                this.itemName = data.displayName;
                this.itemType = data.name;
                this.setElementData(CUSTOM_ATTR_NAME, data.name);
            }

            const index = this.elementsNew.length;

            if (data.name === this.itemType) {
                const newElement = await generateRendering(data, index);
                this.generateElement(this.itemType, newElement);
                this.setChangedData(true);
            } else {
                this.showSnackbar({
                    title: 'Unable to drop.',
                    text: 'You can only drop additional elements of the same type.',
                });
            }
        },

        onItemClassUpdate(classesNew) {
            this.previousClasses = classesNew.join(' ');
        },
    },
};
</script>
